import { forwardRef } from 'react'
import { mergeClasses } from 'minimal-shared/utils'

import { styled } from '@mui/material/styles'
import ButtonBase from '@mui/material/ButtonBase'

import { createNavItem } from '../utils'
import { navItemStyles, navSectionClasses } from '../styles'
import FaIcon from 'src/components/shared/faIcon/FaIcon'

// ----------------------------------------------------------------------

export const NavItem = forwardRef((props, ref) => {
  const {
    path,
    icon,
    info,
    title,
    /********/
    open,
    active,
    disabled,
    /********/
    depth,
    render,
    hasChild,
    slotProps,
    className,
    externalLink,
    onClickNav,
    enabledRootRedirect,
    ...other
  } = props

  const navItem = createNavItem({
    path,
    icon,
    info,
    depth,
    render,
    hasChild,
    externalLink,
    onClickNav,
    enabledRootRedirect
  })

  const ownerState = {
    open,
    active,
    disabled,
    variant: navItem.rootItem ? 'rootItem' : 'subItem'
  }

  return (
    <ItemRoot
      ref={ref}
      aria-label={title}
      {...ownerState}
      className={mergeClasses([navSectionClasses.item.root, className], {
        [navSectionClasses.state.open]: open,
        [navSectionClasses.state.active]: active,
        [navSectionClasses.state.disabled]: disabled
      })}
      sx={slotProps?.sx}
      {...other}
      {...navItem.baseProps}
    >
      {icon && <ItemIcon icon={navItem.renderIcon} />}

      {title && (
        <ItemTitle
          {...ownerState}
          className={navSectionClasses.item.title}
          sx={slotProps?.title}
        >
          {title}
        </ItemTitle>
      )}
    </ItemRoot>
  )
})

// ----------------------------------------------------------------------

const shouldForwardProp = (prop) =>
  !['open', 'active', 'disabled', 'variant', 'sx'].includes(prop)

/**
 * @slot root
 */
const ItemRoot = styled(ButtonBase, { shouldForwardProp })(({
  active,
  open,
  theme
}) => {
  const rootItemStyles = {
    textAlign: 'center',
    flexDirection: 'column',
    minHeight: 'var(--nav-item-root-height)',
    padding: 'var(--nav-item-root-padding)',
    ...(open && {
      color: 'var(--nav-item-root-open-color)',
      backgroundColor: 'var(--nav-item-root-open-bg)'
    }),
    ...(active && {
      color: 'var(--nav-item-root-active-color)',
      backgroundColor: 'var(--nav-item-root-active-bg)',
      '&:hover': { backgroundColor: 'var(--nav-item-root-active-hover-bg)' },
      ...theme.applyStyles('dark', {
        color: 'var(--nav-item-root-active-color-on-dark)'
      })
    })
  }

  const subItemStyles = {
    minHeight: 'var(--nav-item-sub-height)',
    padding: 'var(--nav-item-sub-padding)',
    color: theme.vars.palette.text.secondary,
    ...(open && {
      color: 'var(--nav-item-sub-open-color)',
      backgroundColor: 'var(--nav-item-sub-open-bg)'
    }),
    ...(active && {
      color: 'var(--nav-item-sub-active-color)',
      backgroundColor: 'var(--nav-item-sub-active-bg)'
    })
  }

  return {
    width: '100%',
    color: 'var(--nav-item-color)',
    borderRadius: 'var(--nav-item-radius)',
    '&:hover': { backgroundColor: 'var(--nav-item-hover-bg)' },
    variants: [
      { props: { variant: 'rootItem' }, style: rootItemStyles },
      { props: { variant: 'subItem' }, style: subItemStyles },
      { props: { disabled: true }, style: navItemStyles.disabled }
    ]
  }
})

export const ItemIcon = styled(FaIcon, { shouldForwardProp })(() => ({
  ...navItemStyles.icon,
  width: 'var(--nav-icon-size)',
  height: 'var(--nav-icon-size)',
  margin: 'var(--nav-icon-margin)'
}))

/**
 * @slot title
 */
const ItemTitle = styled('span', { shouldForwardProp })(
  ({ active, theme }) => ({
    ...navItemStyles.title(theme),
    lineHeight: '16px',
    fontSize: theme.typography.pxToRem(10),
    fontWeight: theme.typography.fontWeightSemiBold,
    variants: [
      {
        props: { variant: 'rootItem' },
        style: {
          ...(active && { fontWeight: theme.typography.fontWeightBold })
        }
      },
      {
        props: { variant: 'subItem' },
        style: {
          ...theme.typography.body2,
          fontWeight: theme.typography.fontWeightMedium,
          ...(active && { fontWeight: theme.typography.fontWeightSemiBold })
        }
      }
    ]
  })
)
